import {
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonToolbar,
  useIonModal
} from "@ionic/react";
import { searchOutline } from "ionicons/icons";
import { orderBy } from "lodash";
import { useEffect, useState } from "react";
import LobbyCard from "../components/LobbyCard";
import { useLobbiesStore } from "../stores/lobbies.store";
import { useUsersStore } from "../stores/users.store";
import { useHistory, useRouteMatch } from "react-router";
import ChallengeCard from "../components/ChallengeCard";
import Header from "../components/Header";
import JoinLobby from "./JoinLobby";

const LobbyList: React.FC = () => {
  const type = useRouteMatch<{ type: string }>().params.type || "active";
  const [activeSegment, setActiveSegment] = useState("date");
  const history = useHistory();
  const user: any = useUsersStore(state => state.user);
  const [sortedLobbies, setSortedLobbies] = useState<any[]>([]);
  const [selectedLobby, setSelectedLobby] = useState<any>(null);
  const [lobbies, setLobbies] = useState<any[]>([]);

  const [showJoinLobby, dismissJoinLobby] = useIonModal(JoinLobby, {
    onDismiss: () => dismissJoinLobby(),
    lobby: selectedLobby,
    navigateTo: (path: string) => history.push(path)
  });

  // TODO: Improve data resolution on back state is updated with new data.
  // TODO: Improve reading relevant data from state
  const userLobbies = useLobbiesStore(state =>
    state.lobbies.filter(lobby => lobby.participants.includes(user?.userId))
  );
  const openLobbies = useLobbiesStore(state =>
    state.openLobbies.filter(
      lobby => !lobby.participants.includes(user?.userId)
    )
  );

  useEffect(() => {
    if (type === "active") {
      setLobbies(
        userLobbies.filter(
          lobby =>
            lobby?.started &&
            !lobby?.ended &&
            !(lobby?.quest || lobby?.challenge)
        )
      );
    } else if (type === "past") {
      setLobbies(userLobbies.filter(lobby => lobby?.started && lobby?.ended));
    } else if (type === "upcoming") {
      setLobbies(userLobbies.filter(lobby => !lobby?.started && !lobby?.ended));
    } else if (type === "join") {
      setLobbies(
        openLobbies
          .filter(
            lobby =>
              !lobby?.name?.startsWith("CHALLENGE") &&
              !lobby?.started &&
              !lobby?.ended
          )
          .sort(
            (a, b) =>
              new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
          )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (lobbies) {
      if (activeSegment === "totalPot") {
        setSortedLobbies(orderBy(lobbies, ["totalPot"], ["desc"]));
      } else if (activeSegment === "buyIn") {
        setSortedLobbies(orderBy(lobbies, ["buyIn"], ["desc"]));
      } else if (activeSegment === "date") {
        setSortedLobbies(
          orderBy(
            lobbies,
            [["upcoming", "join"].includes(type) ? "startDate" : "endDate"],
            [type === "past" ? "desc" : "asc"]
          )
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSegment, lobbies]);

  return (
    <IonPage>
      <IonHeader>
        <Header
          back
          title={
            type === "active"
              ? "Active Lobbies"
              : type === "upcoming"
              ? "Upcoming Lobbies"
              : type === "past"
              ? "Past Lobbies"
              : "Join a Lobby"
          }
        />
        <IonToolbar className="p-2">
          <IonSegment
            value={activeSegment}
            className="mx-4 ion-text-center w-full"
          >
            <IonSegmentButton
              className="text-base px-4 py-2"
              value="buyIn"
              onClick={() => setActiveSegment("buyIn")}
            >
              Buy In
            </IonSegmentButton>
            <IonSegmentButton
              className="text-base px-4 py-2"
              value="date"
              onClick={() => setActiveSegment("date")}
            >
              {["upcoming", "join"].includes(type) ? "Start Date" : "End Date"}
            </IonSegmentButton>
            <IonSegmentButton
              className="text-base px-4 py-2"
              value="totalPot"
              onClick={() => setActiveSegment("totalPot")}
            >
              Total Pot
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="bg-primary-90 flex justify-center h-full mb-24">
          <div className="max-w-xl w-full">
            {sortedLobbies.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full">
                <IonIcon icon={searchOutline} className="text-5xl text-white" />
                <p className="text-xl text-white ion-text-center w-1/2">
                  No{" "}
                  <span className="capitalize">
                    {["join", "upcoming"].includes(type) ? "upcoming" : type}
                  </span>{" "}
                  Lobbies {type === "join" && "or you've already joined all"}
                </p>
              </div>
            )}
            {sortedLobbies.map(lobby => (
              <LobbyCard
                lobby={lobby}
                showPlacings={type === "active"}
                key={`${type}-${lobby.id}`}
                join={type === "join"}
                routerLink={`/lobbies/${lobby.id}`}
                selectionMode={type === "join"}
                setSelectedLobby={setSelectedLobby}
                showJoinLobby={showJoinLobby}
              />
            ))}
          </div>
        </div>

        <div className="fixed bottom-0 w-screen p-2">
          {type === "active" && <ChallengeCard />}
          {type === "upcoming" && (
            <IonButton
              color="primary"
              className="rounded-lg w-full"
              routerLink="/lobbyList/join"
            >
              Join a Lobby
            </IonButton>
          )}
          {type === "join" && (
            <IonButton disabled color="primary" className="rounded-lg w-full">
              Host a Lobby
            </IonButton>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default LobbyList;
